import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Input, AutoComplete, Button, Form } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { getCustomId } from 'app/libs/core/Unique';
import withMobileDetecting from 'app/components/HOGs/detectMobileDesign';
import withTranslations from 'app/components/HOGs/withTranslations';

import { publish } from 'app/libs/core/PubSub';
import * as way from 'app/libs/core/PubSubWays';
import { isHeightFull } from 'app/middleware/hooks';
import { isIOS, isAndroid } from 'react-device-detect';
import {
  setFilterScope,
  setRoadTypesFilter,
} from '../../../actions/Search/search.action';

// ANT CSS
import 'antd/dist/antd.less';
import './searchStyle.css';

import { SearchFilters } from './searchFilters';

import {
  SEARCH_BAR_TOUR,
  SEARCH_BAR_ALL,
  SEARCH_DEFAULT_ROAD_TYPE,
  SEARCH_BAR_MEMBER,
} from '../../../constants/mainSearch.constants';
import {
  prepareSize,
  prepareData,
  useEscListener,
  getRoadTypes,
} from './searchUtils';
import userHistoryService from '../../Utility/userHistory.service';
import { userHistory } from '../../../constants/user.constants';
import { searchLoader } from './searchLoader.service';

import { getUserLink, getEntitiyLink } from '../../Utility/utility';
import { history } from '../../../store';

const timer = {
  postponeBlur: null,
  postponeHideList: null,
};
const FOUNED_ITEM_LENGTH = 10;
const RELATED_ITEM_LENGTH = 2;
const searchHistory = userHistoryService(userHistory.SEARCH_MAIN);

async function getMobileCss() {
  await import('./mobileSearch.css');
}

const MainSearch = (props) => {
  const { tr, isMobileDesign } = props;

  const [form] = Form.useForm();

  const [data, setData] = useState({});
  const [preloadedData, setPreloadedData] = useState(null);
  const [isFocused, setFocus] = useState(false);
  const [isListOpen, setListPosition] = useState(false);

  const mainField = useRef(null);

  const { searchBarData: filters, authentication: auth } = useSelector(
    (store) => store
  );
  const { scope, roadTypes } = filters;

  const dispatch = useDispatch();

  const popUpIconItems = {
    setStartPoint: tr['Set start point'],
    setPoi: tr['Set POI'],
    setEndPoint: tr['Set end point'],
    tourStartHere: tr['Tour start here'],
    tourEndHere: tr['Tour end here'],
    addMyPlace: tr['Add to my place'],
    removeMarker: tr['Remove marker'],
  };

  const seatchText = {
    recentSearches: tr['Recent search'],
    relatedSearch: tr['Related search'],
  };

  const mainItemLength = isHeightFull() ? FOUNED_ITEM_LENGTH : 7;
  const dependItemLength = isHeightFull() ? RELATED_ITEM_LENGTH : 1;

  const setMainFieldValue = (val) => {
    form.setFieldsValue({
      mainInput: val,
    });
  };

  const deFocus = () => {
    setFocus(false);
    if (mainField) {
      mainField.current.blur();
    }
  };
  const [setListener, clearListener] = useEscListener(deFocus);

  const cutData = (arr) =>
    arr.length > mainItemLength ? arr.slice(0, mainItemLength) : arr;

  const setDataByScope = (arr, type = scope) => {
    if (type === scope && arr) {
      arr.marker = getCustomId();
    }

    setData((state) => ({
      ...state,
      [type]: cutData(arr),
    }));
  };

  const updateMarker = (obj) => {
    if (scope && obj && obj[scope]) {
      obj[scope].marker = getCustomId();
    }
    return obj;
  };

  const setPreloadDataByScope = (resp, type = scope) => {
    if (type === scope && resp) {
      resp.marker = getCustomId();
    }

    setPreloadedData((state) =>
      state
        ? {
            ...state,
            [type]: cutData(updateMarker([...resp])),
          }
        : { [type]: cutData(updateMarker([...resp])) }
    );
  };

  useEffect(() => {
    const { mainInput: value } = form.getFieldsValue();
    if (value) {
      if (value.length > 1) {
        setData(updateMarker(data));
      } else {
        setData({});
      }
    }
  }, [scope]);

  useEffect(() => {
    const { mainInput: value } = form.getFieldsValue();
    if (value) {
      if (value.length > 1) {
        searchLoader.loadTour(value, getRoadTypes(roadTypes), setDataByScope);
      }
    }
  }, [roadTypes]);

  const searchHandler = (value) => {
    setMainFieldValue(value);
    if (value) {
      if (value.length > 1) {
        searchLoader.load(
          value,
          scope,
          getRoadTypes(roadTypes),
          setDataByScope
        );
      } else {
        setData({});
        searchLoader.cancel();
      }
    } else {
      searchLoader.cancel();
      setData(searchHistory.get());
    }
  };

  const selectHandler = (key, el) => {
    const rel = el.label.props.el;
    const { value, data, type } = el.label.props.el;
    // const { type, data } = rel;
    const { lat, lng } = data;

    setMainFieldValue(value);
    searchLoader.load(
      value,
      scope,
      getRoadTypes(roadTypes),
      setPreloadDataByScope
    );

    deFocus();
    const { user = {} } = auth;
    searchHistory.add(rel, type, user.id);

    if (lat && lng && type === SEARCH_BAR_ALL) {
      publish(way.SEARCH_SET_POINT_ADDRESS, {
        customId: getCustomId(),
        point: { lat, lng, type },
        itemsTitle: popUpIconItems,
      });
    } else if (type === SEARCH_BAR_MEMBER) {
      history.push(getUserLink(data));
    } else {
      history.push(getEntitiyLink(type, value, data.id));
    }

    form.setFieldsValue({
      mainInput: '',
    });
    searchHandler('');
  };

  const handleFocus = () => {
    setListener();
    setFocus(true);
    setListPosition(true);
    const { mainInput: value } = form.getFieldsValue();

    if (value) {
      if (preloadedData) {
        setData(preloadedData);
        const lastResp = preloadedData.address;
        if (lastResp && lastResp.length > 0 && lastResp[0].data)
          // fixed bug with late address responce (better to wait all of them before set to preloaded data)
          setPreloadedData(null);
      }
    } else {
      setData(searchHistory.get());
      if (preloadedData) setPreloadedData(null);
    }
  };

  const handleBlur = () => {
    timer.postponeBlur = setTimeout(() => {
      setFocus(false);
    }, 100);
  };
  const declinePostponeBlur = () => {
    if (timer.postponeBlur) clearTimeout(timer.postponeBlur);
    if (timer.postponeHideList) clearTimeout(timer.postponeHideList);
    if (mainField) {
      mainField.current.focus();
    }
  };

  const resetFilters = () => {
    form.setFieldsValue({
      mainInput: '',
    });
    setMainFieldValue('');
    searchHandler('');
    dispatch(setFilterScope(SEARCH_BAR_ALL));
    dispatch(setRoadTypesFilter(SEARCH_DEFAULT_ROAD_TYPE));
    if (mainField) {
      mainField.current.focus();
    }
  };

  const listHandler = (isOpen) => {
    if (!isOpen) {
      timer.postponeHideList = setTimeout(() => {
        setListPosition(false);
      }, 100);
      clearListener();
    }
  };

  useEffect(() => {
    if (!isFocused) listHandler(false);
  }, [isFocused]);

  const addAdaptiveStyle = () => isMobileDesign && getMobileCss();
  const isTourTab = scope === SEARCH_BAR_TOUR;
  const isFiltersAvailable = () => isFocused || isListOpen;
  const size = prepareSize(isTourTab);
  const { width, offset } = size;
  const wrapperClass = () =>
    isIOS || isAndroid
      ? 'main-search-wrapper compressed'
      : 'main-search-wrapper';

  const getSuffix = () => {
    if (isFocused && !size.isSmalest) {
      return (
        <Button
          shape="circle"
          icon={<ReloadOutlined />}
          style={{ border: 'none' }}
          onClick={resetFilters}
        />
      );
    }
    return null;
  };

  addAdaptiveStyle();
  return (
    <div className={wrapperClass()}>
      <Form form={form}>
        <Form.Item name="mainInput">
          <AutoComplete
            ref={mainField}
            className="main-search"
            dropdownClassName="main-search-dropdown"
            dropdownMatchSelectWidth={width || 460}
            style={{ width, height: 35 }}
            options={prepareData(data, scope, seatchText, dependItemLength)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onSelect={selectHandler}
            onSearch={searchHandler}
            open={isFocused}
            // onDropdownVisibleChange={(open) => console.log('DFDFFD', open)}
            dropdownAlign={{
              offset,
            }}
            listHeight={10000}
          >
            <Input
              className="MainSearchInput"
              placeholder={tr.SEARCH}
              prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              suffix={getSuffix()}
            />
          </AutoComplete>
        </Form.Item>
      </Form>

      {isFiltersAvailable() && (
        <>
          <SearchFilters
            data={filters}
            declinePostponeBlur={declinePostponeBlur}
            size={size}
          />
        </>
      )}
    </div>
  );
};

MainSearch.displayName = 'MainSearch';

export default withTranslations(withMobileDetecting(MainSearch));
